import { useControllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { usePlaybackStore } from "../state/playbackStore";
import { tickForTime } from "../core/timeMap";
import { chordShape } from "../core/theory/chords";
import { ChordDiagram } from "./ChordDiagram";
import { useT } from "../i18n";
import type { ChartMeasure } from "../types/domain";

interface Segment { chord: string | null; beats: number; time: number }

// split a measure into chord blocks: consecutive beats sharing a chord become one block,
// sized by how many beats it spans (so a mid-bar change divides the cell proportionally).
function measureSegments(m: ChartMeasure): Segment[] {
  const segs: Segment[] = [];
  for (const b of m.beats) {
    const last = segs[segs.length - 1];
    if (last && last.chord === b.chord) last.beats++;
    else segs.push({ chord: b.chord, beats: 1, time: b.time });
  }
  return segs.length ? segs : [{ chord: null, beats: 1, time: m.startTime }];
}

// Lead-sheet chord chart: measures laid out as a grid; a measure with a chord change is split into
// blocks. The playing block is highlighted; click any block to seek there.
export function ChordGrid() {
  const c = useControllers();
  const t = useT();
  const chartMeasures = useProjectStore((s) => s.chartMeasures);
  const currentMeasure = usePlaybackStore((s) => s.currentMeasure);
  const currentBeatTime = usePlaybackStore((s) => s.currentBeatTime);
  const playMode = usePlaybackStore((s) => s.playMode);

  if (!chartMeasures.length) {
    return <div className="chord-grid-empty">{t("grid.empty")}</div>;
  }

  const seek = (time: number, measureIdx: number) => {
    const tick = tickForTime(chartMeasures, time);
    c.score.setTickPosition(tick);                          // keep the (hidden) score cursor aligned
    if (playMode === "song") c.activeSource().seek(time);
    usePlaybackStore.getState().setCurrentMeasure(measureIdx);
    usePlaybackStore.getState().setCurrentBeatTime(time);   // highlight the clicked block
  };

  // distinct chords used in the song → example fingering diagrams (chord view only)
  const usedChords: string[] = [];
  for (const m of chartMeasures) for (const b of m.beats) if (b.chord && !usedChords.includes(b.chord)) usedChords.push(b.chord);
  const diagrams = usedChords.map((c) => ({ c, shape: chordShape(c) })).filter((d) => d.shape);

  return (
    <>
    {diagrams.length > 0 && (
      <div className="chord-shapes-wrap">
        <div className="cs-title">{t("grid.shapes")}</div>
        <div className="chord-shapes">
          {diagrams.map((d) => <ChordDiagram key={d.c} frets={d.shape!.frets} label={d.c} />)}
        </div>
      </div>
    )}
    <div className="chord-grid">
      {chartMeasures.map((m, i) => {
        const segs = measureSegments(m);
        // the single active block = last segment whose start time has been reached by the cursor
        let activeSeg = -1;
        if (i === currentMeasure) {
          for (let j = 0; j < segs.length; j++) { if (currentBeatTime >= segs[j].time - 0.02) activeSeg = j; else break; }
        }
        return (
          <div key={i} className={"cg-cell" + (i === currentMeasure ? " on" : "")}>
            <span className="cg-num">{i + 1}</span>
            <div className="cg-blocks">
              {segs.map((s, j) => {
                const active = j === activeSeg && !!s.chord;
                return (
                  <button key={j} className={"cg-block" + (active ? " on" : "") + (s.chord ? "" : " empty")}
                    style={{ flexGrow: s.beats }} onClick={() => seek(s.time, i)} title={`${i + 1}마디`}>
                    {s.chord || "·"}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
    </>
  );
}
